import React, { useState } from "react";
import { motion } from "framer-motion";
import { Plus, Check } from "lucide-react";
import { ADDONS, buildWaLink } from "../data/site";
import { useLang, loc } from "../data/i18n";
import { Waves } from "./Decor";
import { BrandSymbol } from "./BrandLogo";

export default function Addons() {
    const [selected, setSelected] = useState([]);
    const { t, lang } = useLang();

    const toggle = (id) => {
        setSelected((s) =>
            s.includes(id) ? s.filter((x) => x !== id) : [...s, id],
        );
    };

    const nameOf = (id) => {
        const raw = ADDONS.find((a) => a.id === id);
        return raw ? loc(raw, lang).name : null;
    };

    const message =
        selected.length > 0
            ? t.addons.waWith(
                  selected.map(nameOf).filter(Boolean).join(", "),
              )
            : t.addons.waEmpty;

    return (
        <section
            id="addons"
            className="relative bg-lagoon-50 py-24 md:py-32 overflow-hidden"
            data-testid="addons-section"
        >
            <BrandSymbol
                size={280}
                className="absolute -top-16 -right-24 opacity-15"
            />

            <div className="relative max-w-[1400px] mx-auto px-5 md:px-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
                    <div className="lg:col-span-7">
                        <p className="text-[11px] uppercase tracking-[0.3em] text-coral font-bold mb-5 flex items-center gap-3">
                            <span className="inline-block w-8 h-px bg-coral" />
                            {t.addons.kicker}
                        </p>
                        <h2 className="font-serif font-normal text-ocean text-5xl md:text-7xl leading-[0.95] tracking-tight">
                            {t.addons.titleA}
                            <br />
                            <em className="coral-italic">{t.addons.titleB}</em>.
                        </h2>
                    </div>
                    <p className="lg:col-span-5 text-ink/75 text-base leading-relaxed lg:pt-8">
                        {t.addons.intro}
                    </p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                    {ADDONS.map((raw, i) => {
                        const addon = loc(raw, lang);
                        const isSel = selected.includes(addon.id);
                        return (
                            <motion.button
                                key={addon.id}
                                onClick={() => toggle(addon.id)}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: i * 0.06 }}
                                className={`zoom-parent group relative aspect-[4/5] overflow-hidden text-left rounded-lg transition-all duration-300 ${
                                    isSel
                                        ? "ring-4 ring-coral shadow-[0_15px_50px_rgba(235,103,65,0.35)] scale-[1.02]"
                                        : "ring-1 ring-ocean/15 hover:ring-2 hover:ring-ocean/40 shadow-[0_8px_30px_rgba(15,42,63,0.10)]"
                                }`}
                                data-testid={`addon-${addon.id}`}
                                aria-pressed={isSel}
                            >
                                <img
                                    src={addon.image}
                                    alt={addon.name}
                                    className="zoom-img absolute inset-0 w-full h-full object-cover"
                                    loading="lazy"
                                />

                                {/* Harmonised photo treatment: subtle brand-tint + consistent dark gradient bottom */}
                                <div className="absolute inset-0 bg-ocean-900/10 mix-blend-multiply" />
                                <div className="absolute inset-0 bg-gradient-to-t from-ocean-900/92 via-ocean-900/45 to-transparent" />

                                {/* Category badge (matches Experiences style) */}
                                <div className="absolute top-3 left-3 md:top-4 md:left-4">
                                    <span className="text-[9px] uppercase tracking-[0.3em] text-ocean bg-lagoon px-2.5 py-1 font-bold">
                                        {addon.category}
                                    </span>
                                </div>

                                {/* Add / Selected round button */}
                                <div
                                    className={`absolute top-3 right-3 w-10 h-10 rounded-full flex items-center justify-center transition-all shadow-lg ${
                                        isSel
                                            ? "bg-coral text-cream scale-110"
                                            : "bg-cream text-coral group-hover:bg-coral group-hover:text-cream"
                                    }`}
                                    aria-hidden="true"
                                >
                                    {isSel ? (
                                        <Check size={18} strokeWidth={3} />
                                    ) : (
                                        <Plus size={18} strokeWidth={2.5} />
                                    )}
                                </div>

                                {/* Text block */}
                                <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
                                    <h3 className="font-serif text-cream text-xl md:text-2xl mb-1 leading-tight">
                                        {addon.name}
                                    </h3>
                                    <p className="text-cream/85 text-xs leading-snug">
                                        {addon.desc}
                                    </p>
                                </div>
                            </motion.button>
                        );
                    })}
                </div>

                {/* Selection bandeau */}
                <div className="relative mt-12 bg-cream rounded-xl border border-ocean/15 shadow-[0_10px_40px_rgba(15,42,63,0.08)] overflow-hidden">
                    {/* Turquoise wave accent — top border */}
                    <div className="pointer-events-none">
                        <Waves className="w-full h-4" color="#77CABA" />
                    </div>
                    {/* Petal filigree */}
                    <BrandSymbol
                        size={160}
                        tint="coral"
                        className="absolute -right-6 -bottom-8 opacity-15"
                    />

                    <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-6 p-6 md:p-8">
                        <div className="flex items-center gap-5">
                            <div className="w-14 h-14 rounded-full bg-coral text-cream flex items-center justify-center font-serif text-2xl shadow-md">
                                {selected.length}
                            </div>
                            <div>
                                <p className="text-ink/60 text-[10px] uppercase tracking-[0.25em] font-bold mb-1">
                                    {t.addons.selection}
                                </p>
                                <p
                                    className="font-serif text-ocean text-2xl md:text-3xl leading-tight"
                                    data-testid="addons-count"
                                >
                                    {selected.length === 0
                                        ? t.addons.empty
                                        : t.addons.count(selected.length)}
                                </p>
                            </div>
                        </div>
                        <a
                            href={buildWaLink(message)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-coral"
                            data-testid="addons-cta"
                        >
                            {t.addons.cta}
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
}
